// Pure canvas drawing functions for each station cross-section
import { Geo } from './geometry';

type Style = 'schematic' | 'engineering' | 'fea';

const NAVY   = '#1a2b4a';
const BLUE   = '#2563eb';
const AMBER  = '#d97706';
const GREEN  = '#059669';
const STEEL  = '#64748b';
const LIGHT  = '#e2e8f0';

function feaColor(t: number): string {
  const stops = [[10,50,180],[0,170,230],[30,190,70],[230,210,0],[210,35,20]] as const;
  const c = Math.max(0, Math.min(0.999, t));
  const si = Math.min(Math.floor(c * 4), 3);
  const sf = c * 4 - si;
  const a = stops[si], b = stops[si + 1];
  return `rgb(${Math.round(a[0]+(b[0]-a[0])*sf)},${Math.round(a[1]+(b[1]-a[1])*sf)},${Math.round(a[2]+(b[2]-a[2])*sf)})`;
}

function hatch(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, sty: Style, secondary = false) {
  ctx.save();
  ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
  const sp = sty === 'engineering' ? 5 : 8;
  ctx.strokeStyle = sty === 'engineering'
    ? (secondary ? 'rgba(0,0,0,0.12)' : 'rgba(0,0,0,0.18)')
    : sty === 'fea' ? 'rgba(255,255,255,0.07)' : 'rgba(37,99,235,0.09)';
  ctx.lineWidth = 0.4;
  for (let i = -(h + w); i < w + h; i += sp) {
    ctx.beginPath(); ctx.moveTo(x + i, y); ctx.lineTo(x + i + h, y + h); ctx.stroke();
  }
  ctx.restore();
}

function hexPts(cx: number, cy: number, r: number): [number,number][] {
  return Array.from({length:6}, (_, i) => {
    const a = i * Math.PI / 3 - Math.PI / 6;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as [number,number];
  });
}

function drawHexOutline(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
  const pts = hexPts(cx, cy, r);
  ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
  pts.forEach(([x,y]) => ctx.lineTo(x, y)); ctx.closePath();
}

function bodyFill(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, sty: Style, strain: number, isShank = false) {
  if (sty === 'fea') {
    const g = ctx.createLinearGradient(x, y, x + w, y + h);
    g.addColorStop(0, feaColor(strain - 0.06));
    g.addColorStop(1, feaColor(strain));
    ctx.fillStyle = g;
  } else if (sty === 'engineering') {
    ctx.fillStyle = isShank ? '#d4d8de' : '#e0e4ea';
  } else {
    ctx.fillStyle = isShank ? 'rgba(37,99,235,0.07)' : 'rgba(37,99,235,0.13)';
  }
}

function strokePart(ctx: CanvasRenderingContext2D, sty: Style, done: boolean) {
  ctx.strokeStyle = sty === 'engineering' ? '#1a2b4a' : done ? GREEN : (sty === 'fea' ? '#fff' : NAVY);
  ctx.lineWidth = sty === 'engineering' ? 1.2 : 0.9;
}

function dimLine(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, label: string, isH = true) {
  ctx.strokeStyle = '#374151'; ctx.lineWidth = 0.6; ctx.setLineDash([2, 2]);
  ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  ctx.setLineDash([]);
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
  ctx.fillStyle = '#374151'; ctx.font = '8px Inter,sans-serif'; ctx.textAlign = 'center';
  if (isH) ctx.fillText(label, mx, y1 - 3);
  else {
    ctx.save(); ctx.translate(mx + 10, my); ctx.rotate(-Math.PI / 2);
    ctx.fillText(label, 0, 0); ctx.restore();
  }
}

// ---- MAIN DRAW FUNCTION ----
export function drawStation(canvas: HTMLCanvasElement, geo: Geo, sty: Style, highlight = false) {
  const ctx = canvas.getContext('2d')!;
  const W = canvas.width, H = canvas.height;
  ctx.clearRect(0, 0, W, H);

  // Background
  if (sty === 'engineering') {
    ctx.fillStyle = '#f8f9fb'; ctx.fillRect(0, 0, W, H);
    // Drawing border
    ctx.strokeStyle = '#c8cdd8'; ctx.lineWidth = 0.8;
    ctx.strokeRect(4, 4, W - 8, H - 8);
  } else if (sty === 'fea') {
    ctx.fillStyle = '#0f1623'; ctx.fillRect(0, 0, W, H);
  } else {
    ctx.fillStyle = highlight ? '#eff6ff' : '#ffffff';
    ctx.fillRect(0, 0, W, H);
    if (highlight) { ctx.strokeStyle = '#bfdbfe'; ctx.lineWidth = 1.5; ctx.strokeRect(0, 0, W, H); }
  }

  const cx = W / 2;

  if (geo.type === 'BOLT')    drawBolt(ctx, cx, H, W, H, geo, sty);
  else if (geo.type === 'NUT') drawNut(ctx, cx, H / 2, W, H, geo, sty);
  else                          drawSetscrew(ctx, cx, H / 2, W, H, geo, sty);

  // Station label top
  const lc = sty === 'engineering' ? '#1a2b4a' : sty === 'fea' ? '#94a3b8' : '#374151';
  ctx.fillStyle = lc; ctx.font = `600 10px Inter,sans-serif`; ctx.textAlign = 'center';
  ctx.fillText(geo.label, cx, 14);
}

function drawBolt(ctx: CanvasRenderingContext2D, cx: number, fullH: number, W: number, H: number, geo: Geo, sty: Style) {
  if (geo.isCutoff) {
    // Wire blank — vertical cylinder
    const sc = Math.min(2.8, 40 / (geo.wireD ?? 10));
    const bw = Math.min((geo.wireD ?? 10) * sc, W - 24);
    const bh = Math.min((geo.blankL ?? 30) * sc * 0.5, H - 50);
    const bx = cx - bw / 2, by = H / 2 - bh / 2;
    bodyFill(ctx, bx, by, bw, bh, sty, 0);
    ctx.fillRect(bx, by, bw, bh);
    hatch(ctx, bx, by, bw, bh, sty);
    strokePart(ctx, sty, false);
    ctx.strokeRect(bx, by, bw, bh);
    if (sty === 'engineering') { dimLine(ctx, bx, by - 10, bx + bw, by - 10, `Ø${(geo.wireD??0).toFixed(2)}`); }
    return;
  }

  const done = geo.stn >= 4 || (geo.hasTrimDie ?? false);
  const sc = 2.8;

  const headD = geo.headD ?? 10;
  const headH = geo.headH ?? 8;
  const shankD = geo.shankD ?? 8;
  const shankL = geo.shankL ?? 30;

  const hW = Math.min(headD * sc / 2, W / 2 - 4);
  const hH = Math.min(headH * sc * 0.85, H * 0.28);
  const sW = Math.min(shankD * sc / 2, hW * 0.88);
  const sL = Math.min(shankL * sc * 0.55, H * 0.52);
  const flH = geo.hasFlange ? Math.min((geo.flangeH ?? 4) * sc * 0.7, hH * 0.42) : 0;
  const flangeW = geo.hasFlange ? Math.min((geo.flangeD ?? headD) * sc / 2, W / 2 - 2) : 0;

  // Position: head at top, shank hangs down
  const headY = 26;
  const shankY = headY + hH;
  const totalH = headY + hH + sL + (geo.hasPoint ? 10 : 0);
  const yOff = Math.max(0, (H - totalH) / 2);
  const hy = headY + yOff;
  const sy = shankY + yOff;

  // === FLANGE ===
  if (geo.hasFlange && flangeW > 0) {
    const fy = hy + hH - flH;
    bodyFill(ctx, cx - flangeW, fy, flangeW * 2, flH, sty, (geo.strainMax ?? 0.5) * 0.9);
    ctx.fillRect(cx - flangeW, fy, flangeW * 2, flH); ctx.fill();
    hatch(ctx, cx - flangeW, fy, flangeW * 2, flH, sty);
    // Serration teeth on flange underside
    if (geo.hasSerration) {
      ctx.strokeStyle = sty === 'engineering' ? '#374151' : AMBER; ctx.lineWidth = 0.5;
      const teethCount = 18;
      const tw = (flangeW * 2) / teethCount;
      for (let i = 0; i < teethCount; i++) {
        const tx = cx - flangeW + i * tw;
        ctx.beginPath(); ctx.moveTo(tx, fy + flH); ctx.lineTo(tx + tw / 2, fy + flH + 4); ctx.lineTo(tx + tw, fy + flH);
        ctx.stroke();
      }
    }
    strokePart(ctx, sty, done);
    ctx.strokeRect(cx - flangeW, fy, flangeW * 2, flH);
  }

  // === HEAD ===
  bodyFill(ctx, cx - hW, hy, hW * 2, hH, sty, geo.strainMax ?? 0.5);
  ctx.fillRect(cx - hW, hy, hW * 2, hH);
  hatch(ctx, cx - hW, hy, hW * 2, hH, sty);

  // Dome on top of head (Stn 4 final)
  if (geo.hasDome) {
    ctx.beginPath(); ctx.ellipse(cx, hy, hW * 0.38, hW * 0.18, 0, Math.PI, 0);
    ctx.fillStyle = sty === 'engineering' ? '#d0d6de' : sty === 'fea' ? feaColor(0.6) : 'rgba(37,99,235,0.15)';
    ctx.fill();
    ctx.strokeStyle = sty === 'engineering' ? NAVY : (sty === 'fea' ? '#fff' : BLUE); ctx.lineWidth = 0.7; ctx.stroke();
  }

  // Head outline
  strokePart(ctx, sty, done);
  ctx.strokeRect(cx - hW, hy, hW * 2, hH);

  // Hex socket
  if (geo.socketAF && geo.socketDepth) {
    const sr = Math.min(geo.socketAF * sc / 2, hW * 0.56);
    const sd = Math.min(geo.socketDepth * sc * 0.8, hH * 0.65);
    // Hex cavity
    drawHexOutline(ctx, cx, hy + sd * 0.45, sr);
    ctx.fillStyle = sty === 'engineering' ? '#ffffff' : sty === 'fea' ? '#000' : '#f8faff';
    ctx.fill();
    drawHexOutline(ctx, cx, hy + sd * 0.45, sr);
    ctx.strokeStyle = sty === 'engineering' ? '#374151' : sty === 'fea' ? '#334155' : '#93c5fd';
    ctx.lineWidth = 0.7; ctx.stroke();
    // Entry chamfer lines
    ctx.setLineDash([2, 2]);
    ctx.strokeStyle = sty === 'engineering' ? '#9ca3af' : 'rgba(37,99,235,0.3)'; ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(cx - sr, hy); ctx.lineTo(cx - sr, hy + sd * 0.1); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx + sr, hy); ctx.lineTo(cx + sr, hy + sd * 0.1); ctx.stroke();
    ctx.setLineDash([]);
    if (sty === 'engineering') dimLine(ctx, cx - sr, hy - 8, cx + sr, hy - 8, `A/F ${(geo.socketAF).toFixed(2)}`);
  }

  // Cone mark (Stn 1 Template A)
  if (geo.coneAngle && geo.stn === 1) {
    const ang = geo.coneAngle * Math.PI / 180;
    const tipY = hy - hW * Math.tan(ang) * 0.6;
    ctx.beginPath(); ctx.moveTo(cx - hW * 0.5, hy); ctx.lineTo(cx, tipY); ctx.lineTo(cx + hW * 0.5, hy);
    ctx.fillStyle = sty === 'engineering' ? '#c5d0e0' : sty === 'fea' ? feaColor(0.2) : 'rgba(37,99,235,0.18)';
    ctx.fill();
    ctx.strokeStyle = sty === 'engineering' ? NAVY : AMBER; ctx.lineWidth = 0.8; ctx.stroke();
    ctx.fillStyle = sty === 'engineering' ? '#374151' : AMBER; ctx.font = '8px Inter,sans-serif';
    ctx.textAlign = 'left'; ctx.fillText(`${geo.coneAngle}°`, cx + hW * 0.5 + 3, tipY + 6);
    ctx.textAlign = 'center';
  }

  // === SHANK ===
  bodyFill(ctx, cx - sW, sy, sW * 2, sL, sty, (geo.strainMin ?? 0.1) * 1.2, true);
  ctx.fillRect(cx - sW, sy, sW * 2, sL);
  hatch(ctx, cx - sW, sy, sW * 2, sL, sty, true);
  strokePart(ctx, sty, done);
  ctx.strokeRect(cx - sW, sy, sW * 2, sL);

  // Centre line
  ctx.setLineDash([3, 3]);
  ctx.strokeStyle = sty === 'engineering' ? '#9ca3af' : 'rgba(37,99,235,0.2)'; ctx.lineWidth = 0.4;
  ctx.beginPath(); ctx.moveTo(cx, hy - 4); ctx.lineTo(cx, sy + sL + (geo.hasPoint ? 10 : 4)); ctx.stroke();
  ctx.setLineDash([]);

  // Point
  if (geo.hasPoint) {
    const pL = 10;
    ctx.beginPath(); ctx.moveTo(cx - sW, sy + sL); ctx.lineTo(cx, sy + sL + pL); ctx.lineTo(cx + sW, sy + sL);
    ctx.fillStyle = sty === 'engineering' ? '#c8cdd8' : sty === 'fea' ? feaColor((geo.strainMin ?? 0.1)) : 'rgba(37,99,235,0.10)';
    ctx.fill(); ctx.strokeStyle = sty === 'engineering' ? NAVY : (done ? GREEN : NAVY); ctx.lineWidth = 0.8; ctx.stroke();
  }

  // Dimension lines
  if (sty === 'engineering' && !geo.isCutoff) {
    dimLine(ctx, cx - hW, hy - 10, cx + hW, hy - 10, `Ø${(headD).toFixed(1)}`);
    dimLine(ctx, cx + hW + 10, sy, cx + hW + 10, sy + sL, `${(shankL).toFixed(0)}`, false);
  }

  // FEA strain bar (right side)
  if (sty === 'fea' && !geo.isCutoff) {
    const barH = Math.min(sL + hH, H - 40);
    const barW = 8, bx = W - 18, by2 = (H - barH) / 2;
    for (let i = 0; i < barH; i++) {
      ctx.fillStyle = feaColor(1 - i / barH);
      ctx.fillRect(bx, by2 + i, barW, 1);
    }
    ctx.fillStyle = '#94a3b8'; ctx.font = '7px Inter,sans-serif'; ctx.textAlign = 'right';
    ctx.fillText('Hi', bx - 2, by2 + 8);
    ctx.fillText('Lo', bx - 2, by2 + barH);
  }

  // Top Nib indicator
  if (geo.hasTopNib) {
    ctx.fillStyle = sty === 'engineering' ? '#9ca3af' : AMBER; ctx.font = '7px Inter,sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('↑ Nib', cx, hy + hH - 3);
  }
}

function drawNut(ctx: CanvasRenderingContext2D, cx: number, cy: number, W: number, H: number, geo: Geo, sty: Style) {
  if (geo.isCutoff) {
    // Wire circle (cross-section top view + side view)
    const r = Math.min((geo.nutAF ?? 8) * 2, 28);
    ctx.beginPath(); ctx.arc(cx, cy - 8, r, 0, Math.PI * 2);
    ctx.fillStyle = sty === 'engineering' ? '#d0d8e4' : sty === 'fea' ? feaColor(0.08) : '#dbeafe';
    ctx.fill(); hatch(ctx, cx - r, cy - 8 - r, r * 2, r * 2, sty);
    ctx.strokeStyle = sty === 'engineering' ? NAVY : BLUE; ctx.lineWidth = 1; ctx.stroke();
    ctx.fillStyle = sty === 'engineering' ? '#374151' : '#475569'; ctx.font = '8px Inter,sans-serif'; ctx.textAlign = 'center';
    ctx.fillText(`Wire Ø${(geo.nutAF ?? 0).toFixed(1)}`, cx, cy + r + 4);
    return;
  }

  const done = geo.isPierced || (geo.nutAF ?? 0) > 14;
  const sc = Math.min(3.4, 50 / (geo.nutAF ?? 10));
  const af = (geo.nutAF ?? 10) * sc / 2;
  const h  = Math.min((geo.nutH ?? 8) * sc, H * 0.42);
  const ch = Math.min((geo.topChamfer ?? 1.2) * sc, h * 0.16);
  const boreR = geo.boreD ? (geo.boreD * sc / 2) : 0;
  const ny = cy - h / 2;
  const strain = geo.strainMax ?? 0.4;

  // === NUT BODY — side view ===
  if (geo.isHex) {
    // Hex profile with chamfers top/bottom
    ctx.beginPath();
    ctx.moveTo(cx - af + ch, ny); ctx.lineTo(cx + af - ch, ny);
    ctx.lineTo(cx + af, ny + ch); ctx.lineTo(cx + af, ny + h - ch);
    ctx.lineTo(cx + af - ch, ny + h); ctx.lineTo(cx - af + ch, ny + h);
    ctx.lineTo(cx - af, ny + h - ch); ctx.lineTo(cx - af, ny + ch); ctx.closePath();

    if (sty === 'fea') {
      const g = ctx.createRadialGradient(cx, cy, boreR || 2, cx, cy, af);
      g.addColorStop(0, feaColor(strain - 0.15)); g.addColorStop(1, feaColor(strain));
      ctx.fillStyle = g;
    } else if (sty === 'engineering') { ctx.fillStyle = '#d8dce4'; }
    else { ctx.fillStyle = done ? '#ecfdf5' : '#eff6ff'; }
    ctx.fill(); hatch(ctx, cx - af, ny, af * 2, h, sty);
    ctx.strokeStyle = sty === 'engineering' ? NAVY : (done ? GREEN : BLUE);
    ctx.lineWidth = sty === 'engineering' ? 1.1 : 0.9;

    ctx.beginPath();
    ctx.moveTo(cx - af + ch, ny); ctx.lineTo(cx + af - ch, ny);
    ctx.lineTo(cx + af, ny + ch); ctx.lineTo(cx + af, ny + h - ch);
    ctx.lineTo(cx + af - ch, ny + h); ctx.lineTo(cx - af + ch, ny + h);
    ctx.lineTo(cx - af, ny + h - ch); ctx.lineTo(cx - af, ny + ch); ctx.closePath();
    ctx.stroke();
  } else {
    // Round (before hex forms)
    bodyFill(ctx, cx - af, ny, af * 2, h, sty, strain);
    ctx.fillRect(cx - af, ny, af * 2, h); hatch(ctx, cx - af, ny, af * 2, h, sty);
    strokePart(ctx, sty, done);
    ctx.strokeRect(cx - af, ny, af * 2, h);
  }

  // Bore (pierced)
  if (boreR > 0) {
    ctx.fillStyle = sty === 'engineering' ? '#f8f9fb' : sty === 'fea' ? '#0f1623' : '#ffffff';
    ctx.fillRect(cx - boreR, ny, boreR * 2, h);
    ctx.strokeStyle = sty === 'engineering' ? '#374151' : '#60a5fa'; ctx.lineWidth = 0.6;
    ctx.strokeRect(cx - boreR, ny, boreR * 2, h);
    // Centre line
    ctx.setLineDash([3, 3]); ctx.strokeStyle = '#9ca3af'; ctx.lineWidth = 0.4;
    ctx.beginPath(); ctx.moveTo(cx, ny - 6); ctx.lineTo(cx, ny + h + 6); ctx.stroke();
    ctx.setLineDash([]);
    if (sty === 'engineering') {
      dimLine(ctx, cx - boreR, ny + h + 8, cx + boreR, ny + h + 8, `Ø${(geo.boreD ?? 0).toFixed(2)}`);
    }
  }

  // Active KO arrow
  if (geo.activeKO && !geo.isPierced) {
    const ax = cx + af + 10;
    ctx.beginPath(); ctx.moveTo(ax, ny + h + 4); ctx.lineTo(ax - 5, ny + h + 14); ctx.lineTo(ax + 5, ny + h + 14); ctx.closePath();
    ctx.fillStyle = sty === 'fea' ? '#10b981' : GREEN; ctx.fill();
    ctx.fillStyle = sty === 'fea' ? '#10b981' : GREEN; ctx.font = '7px Inter,sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('KO', ax, ny + h + 24);
  }

  // Hex top-view indicator (small hexagon above)
  if (geo.isHex) {
    const hr = Math.min(af * 0.48, 20);
    const topY = ny - hr * 1.4 - 6;
    drawHexOutline(ctx, cx, topY, hr);
    ctx.fillStyle = sty === 'engineering' ? '#e8ecf2' : sty === 'fea' ? '#1e3a5f' : '#dbeafe'; ctx.fill();
    ctx.strokeStyle = sty === 'engineering' ? NAVY : (done ? GREEN : BLUE); ctx.lineWidth = 0.7; ctx.stroke();
    // Mark "A/F"
    if (sty !== 'fea') {
      ctx.fillStyle = '#6b7280'; ctx.font = '7px Inter,sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(`A/F ${(geo.nutAF ?? 0).toFixed(1)}`, cx, topY + hr + 10);
    }
  }

  // Dimension
  if (sty === 'engineering' && !geo.isCutoff) {
    dimLine(ctx, cx - af, ny - (geo.isHex ? 30 : 10), cx + af, ny - (geo.isHex ? 30 : 10), `Ø${((geo.nutAF??0)*2/sc*sc/sc).toFixed(1)}`);
    dimLine(ctx, cx + af + 18, ny, cx + af + 18, ny + h, `${(geo.nutH ?? 0).toFixed(1)}`, false);
  }
}

function drawSetscrew(ctx: CanvasRenderingContext2D, cx: number, cy: number, W: number, H: number, geo: Geo, sty: Style) {
  if (geo.isCutoff) {
    const sc = Math.min(3.5, 40 / (geo.bodyD ?? 10));
    const bw = (geo.bodyD ?? 10) * sc, bh = Math.min((geo.bodyL ?? 20) * sc * 0.5, H - 50);
    const bx = cx - bw / 2, by = cy - bh / 2;
    bodyFill(ctx, bx, by, bw, bh, sty, 0);
    ctx.fillRect(bx, by, bw, bh); hatch(ctx, bx, by, bw, bh, sty);
    ctx.strokeStyle = sty === 'engineering' ? NAVY : BLUE; ctx.lineWidth = 0.8;
    ctx.strokeRect(bx, by, bw, bh);
    return;
  }
  const sc = Math.min(3.5, 40 / (geo.bodyD ?? 10));
  const bw = (geo.bodyD ?? 10) * sc / 2;
  const bh = Math.min((geo.bodyL ?? 20) * sc * 0.5, H * 0.55);
  const by = cy - bh / 2;
  const strain = geo.strainMax ?? 0.3;

  bodyFill(ctx, cx - bw, by, bw * 2, bh, sty, strain);
  ctx.fillRect(cx - bw, by, bw * 2, bh); hatch(ctx, cx - bw, by, bw * 2, bh, sty);
  ctx.strokeStyle = sty === 'engineering' ? NAVY : AMBER; ctx.lineWidth = 0.9;
  ctx.strokeRect(cx - bw, by, bw * 2, bh);

  // Centre line
  ctx.setLineDash([3, 3]); ctx.strokeStyle = '#9ca3af'; ctx.lineWidth = 0.4;
  ctx.beginPath(); ctx.moveTo(cx, by - 4); ctx.lineTo(cx, by + bh + 4); ctx.stroke();
  ctx.setLineDash([]);

  // Top cone / socket
  if (geo.topCone || geo.socketAF) {
    const cr = Math.min(bw * 0.58, 14);
    const cd = bh * 0.22;
    ctx.beginPath(); ctx.moveTo(cx - cr, by); ctx.lineTo(cx, by + cd); ctx.lineTo(cx + cr, by); ctx.closePath();
    ctx.fillStyle = sty === 'engineering' ? '#f0f2f5' : sty === 'fea' ? '#0f1623' : '#f0f9ff'; ctx.fill();
    ctx.strokeStyle = sty === 'engineering' ? '#374151' : AMBER; ctx.lineWidth = 0.7; ctx.stroke();
    if (geo.socketAF) {
      const sr = Math.min(geo.socketAF * sc / 2, cr * 0.9);
      drawHexOutline(ctx, cx, by + cd * 0.55, sr);
      ctx.fillStyle = sty === 'engineering' ? '#fff' : '#0f1623'; ctx.fill();
      ctx.strokeStyle = sty === 'engineering' ? '#374151' : '#60a5fa'; ctx.lineWidth = 0.6; ctx.stroke();
    }
    if (geo.topCone && sty !== 'fea') {
      ctx.fillStyle = AMBER; ctx.font = '8px Inter,sans-serif'; ctx.textAlign = 'left';
      ctx.fillText(`${geo.topCone}°`, cx + cr + 2, by + 8); ctx.textAlign = 'center';
    }
  }

  // Bottom cup point
  if (geo.hasCupPoint || geo.botCone) {
    const cr2 = bw * 0.48, cd2 = bh * 0.18;
    ctx.beginPath(); ctx.moveTo(cx - cr2, by + bh); ctx.lineTo(cx, by + bh - cd2); ctx.lineTo(cx + cr2, by + bh); ctx.closePath();
    ctx.fillStyle = sty === 'engineering' ? '#f0f2f5' : sty === 'fea' ? '#0f1623' : '#f0f9ff'; ctx.fill();
    ctx.strokeStyle = sty === 'engineering' ? '#374151' : AMBER; ctx.lineWidth = 0.7; ctx.stroke();
  }

  // Stripper label
  if (geo.hasStripper) {
    ctx.fillStyle = GREEN; ctx.font = '600 8px Inter,sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('STRIPPER', cx, by - 8);
  }

  if (sty === 'engineering') dimLine(ctx, cx - bw, by - 8, cx + bw, by - 8, `Ø${(geo.bodyD ?? 0).toFixed(2)}`);
}
